(() => {
  const SECTION_SELECTOR = '[data-events-archive]';

  const parseUrl = (value) => {
    try {
      return new URL(value);
    } catch (_error) {
      return null;
    }
  };

  const parseYouTubeId = (value) => {
    const parsed = parseUrl(value);
    if (!parsed) return null;

    const host = parsed.hostname.replace(/^www\./, '');
    const pathname = parsed.pathname;

    if (host === 'youtu.be') {
      const id = pathname.split('/').filter(Boolean)[0];
      return id || null;
    }

    if (host.endsWith('youtube.com')) {
      const vParam = parsed.searchParams.get('v');
      if (vParam) return vParam;

      const segments = pathname.split('/').filter(Boolean);
      const embedIndex = segments.findIndex((segment) => segment === 'embed');
      if (embedIndex !== -1 && segments[embedIndex + 1]) {
        return segments[embedIndex + 1];
      }

      const shortsIndex = segments.findIndex((segment) => segment === 'shorts');
      if (shortsIndex !== -1 && segments[shortsIndex + 1]) {
        return segments[shortsIndex + 1];
      }
    }

    return null;
  };

  const parseVimeoId = (value) => {
    const parsed = parseUrl(value);
    if (!parsed) return null;

    const host = parsed.hostname.replace(/^www\./, '');
    if (!host.endsWith('vimeo.com')) return null;

    const segments = parsed.pathname.split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (/^\d+$/.test(segments[i])) return segments[i];
    }

    return null;
  };

  const parseProvider = (value) => {
    const youtubeId = parseYouTubeId(value);
    if (youtubeId) return { provider: 'youtube', id: youtubeId };

    const vimeoId = parseVimeoId(value);
    if (vimeoId) return { provider: 'vimeo', id: vimeoId };

    return null;
  };

  const thumbnailFromProvider = (provider, id) => {
    if (provider === 'youtube') return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    if (provider === 'vimeo') return `https://vumbnail.com/${id}.jpg`;
    return null;
  };

  const embedFromProvider = (provider, id) => {
    if (provider === 'youtube') {
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`;
    }
    if (provider === 'vimeo') {
      return `https://player.vimeo.com/video/${id}?autoplay=1&api=1`;
    }
    return null;
  };

  const isYouTubeEmbed = (url) => typeof url === 'string' && url.includes('youtube.com/embed/');
  const isVimeoEmbed = (url) => typeof url === 'string' && url.includes('player.vimeo.com/video/');

  const pauseIframeVideo = (iframe, embedUrl) => {
    if (!iframe || !iframe.contentWindow) return;
    if (isYouTubeEmbed(embedUrl)) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
      return;
    }
    if (isVimeoEmbed(embedUrl)) {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  };

  const playIframeVideo = (iframe, embedUrl) => {
    if (!iframe || !iframe.contentWindow) return;
    if (isYouTubeEmbed(embedUrl)) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        '*'
      );
      return;
    }
    if (isVimeoEmbed(embedUrl)) {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
    }
  };

  const sanitizeMessage = (value) => {
    if (!value) return '';
    const temp = document.createElement('div');
    temp.innerHTML = String(value);
    return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
  };

  const resolveMailchimpUrl = (actionUrl, audienceU, audienceId) => {
    const parsed = parseUrl(actionUrl);
    if (!parsed) return null;

    parsed.pathname = parsed.pathname.replace(/\/post$/i, '/post-json');
    const resolvedU = audienceU || parsed.searchParams.get('u') || '';
    const resolvedId = audienceId || parsed.searchParams.get('id') || '';
    if (!resolvedU || !resolvedId) return null;

    parsed.searchParams.set('u', resolvedU);
    parsed.searchParams.set('id', resolvedId);
    return parsed;
  };

  const submitMailchimpJsonp = ({ actionUrl, u, id, email, tags }) =>
    new Promise((resolve, reject) => {
      const parsed = resolveMailchimpUrl(actionUrl, u, id);
      if (!parsed) {
        reject(new Error('Mailchimp settings are incomplete.'));
        return;
      }

      const callbackName = `eventsArchiveMc_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      parsed.searchParams.set('EMAIL', email);
      if (tags) parsed.searchParams.set('tags', tags);
      parsed.searchParams.set('c', callbackName);

      const script = document.createElement('script');
      let settled = false;
      let timeoutId = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        delete window[callbackName];
        script.remove();
      };

      const fail = (message) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(sanitizeMessage(message) || 'Unable to sign up right now.'));
      };

      window[callbackName] = (response) => {
        if (settled) return;
        const result = response?.result;
        if (result === 'success') {
          settled = true;
          cleanup();
          resolve(response);
          return;
        }
        fail(response?.msg || 'Unable to sign up right now.');
      };

      script.onerror = () => fail('Unable to connect to Mailchimp.');
      script.src = parsed.toString();
      document.body.appendChild(script);
      timeoutId = window.setTimeout(() => fail('Signup request timed out.'), 12000);
    });

  const setCardThumbnail = (card, url) => {
    if (!url) return false;
    card.classList.add('has-thumbnail');
    card.style.setProperty('--events-thumb-url', `url("${url}")`);
    return true;
  };

  const initCards = (section) => {
    const cards = section.querySelectorAll('.events-archive__past-card[data-video-url]');
    cards.forEach((card) => {
      const videoUrl = card.getAttribute('data-video-url') || '';
      const overrideThumb = card.getAttribute('data-thumbnail-override') || '';
      const fallbackThumb = card.getAttribute('data-fallback-thumbnail') || '';

      let providerData = null;
      let embedUrl = null;
      let thumbnailUrl = '';

      if (videoUrl.trim()) {
        providerData = parseProvider(videoUrl);
        if (providerData) {
          embedUrl = embedFromProvider(providerData.provider, providerData.id);
          card.setAttribute('data-embed-url', embedUrl || '');
        }
      }

      if (!overrideThumb && providerData) {
        thumbnailUrl = thumbnailFromProvider(providerData.provider, providerData.id) || '';
        setCardThumbnail(card, thumbnailUrl);
      } else if (overrideThumb) {
        thumbnailUrl = overrideThumb;
        setCardThumbnail(card, thumbnailUrl);
      } else if (fallbackThumb) {
        thumbnailUrl = fallbackThumb;
        setCardThumbnail(card, thumbnailUrl);
      }

      if (!embedUrl && card.tagName.toLowerCase() === 'button') {
        card.classList.add('is-disabled');
        card.setAttribute('disabled', 'disabled');
        card.setAttribute('aria-disabled', 'true');
      }
    });
  };

  const initModal = (section) => {
    const sectionId = (section.id || '').replace('EventsArchive-', '');
    const modalSelector = sectionId
      ? `[data-events-modal-for="${sectionId}"]`
      : '[data-events-modal]';
    const modal = section.querySelector('[data-events-modal]') || document.querySelector(modalSelector);
    const iframe = modal?.querySelector('[data-events-iframe]');
    const closeElements = modal?.querySelectorAll('[data-events-close]');
    const modalTitle = modal?.querySelector('.events-archive__modal-title');
    const modalDetails = modal?.querySelector('[data-events-modal-details]');

    if (!modal || !iframe || !closeElements || !modalTitle || !modalDetails) return;

    // Move modal to body so it always sits above section/card stacking contexts.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    let lastTrigger = null;
    const detailOrder = (section.getAttribute('data-modal-detail-order') || 'name,location,date')
      .split(',')
      .map((token) => token.trim());

    const formatModalDetails = (fields) => {
      const location = fields.location || '';
      const date = fields.date || '';

      const locationIndex = detailOrder.indexOf('location');
      const dateIndex = detailOrder.indexOf('date');
      const useDateFirst =
        date &&
        location &&
        dateIndex !== -1 &&
        (locationIndex === -1 || dateIndex < locationIndex);

      const orderedDetails = useDateFirst ? [date, location] : [location, date];
      return orderedDetails.filter((value) => value).join(' - ');
    };

    const closeModal = () => {
      modal.setAttribute('hidden', '');
      pauseIframeVideo(iframe, iframe.dataset.currentEmbedUrl || iframe.getAttribute('src') || '');
      document.body.classList.remove('events-archive-modal-open');
      document.removeEventListener('keydown', onEscape);
      if (lastTrigger) lastTrigger.focus();
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    const openModal = (embedUrl, eventDetails, trigger) => {
      if (!embedUrl) return;
      lastTrigger = trigger;
      modal.removeAttribute('hidden');
      modalTitle.textContent = eventDetails.name || 'Event Video';
      modalDetails.textContent = formatModalDetails(eventDetails) || eventDetails.name || '';
      const currentEmbedUrl = iframe.dataset.currentEmbedUrl || '';
      if (currentEmbedUrl !== embedUrl) {
        iframe.setAttribute('src', embedUrl);
        iframe.dataset.currentEmbedUrl = embedUrl;
      } else {
        playIframeVideo(iframe, embedUrl);
      }
      document.body.classList.add('events-archive-modal-open');
      document.addEventListener('keydown', onEscape);
      const closeButton = modal.querySelector('.events-archive__modal-close');
      if (closeButton) closeButton.focus();
    };

    closeElements.forEach((node) => {
      node.addEventListener('click', closeModal);
    });

    const triggers = section.querySelectorAll('.events-archive__past-card[data-embed-url]');
    triggers.forEach((trigger) => {
      if (trigger.getAttribute('disabled') === 'disabled') return;
      trigger.addEventListener('click', () => {
        openModal(
          trigger.getAttribute('data-embed-url') || '',
          {
            name: trigger.getAttribute('data-event-title') || '',
            location: trigger.getAttribute('data-event-location') || '',
            date: trigger.getAttribute('data-event-date') || '',
          },
          trigger
        );
      });
    });
  };

  const initSignupModal = (section) => {
    const sectionId = (section.id || '').replace('EventsArchive-', '');
    const modalSelector = sectionId
      ? `[data-events-signup-modal-for="${sectionId}"]`
      : '[data-events-signup-modal]';
    const modal =
      section.querySelector('[data-events-signup-modal]') || document.querySelector(modalSelector);
    const closeElements = modal?.querySelectorAll('[data-events-signup-close]');
    const form = modal?.querySelector('[data-signup-form]');
    const emailInput = modal?.querySelector('[data-signup-email]');
    const submitButton = modal?.querySelector('[data-signup-submit]');
    const feedback = modal?.querySelector('[data-signup-feedback]');
    const eventContext = modal?.querySelector('[data-signup-event-context]');

    if (!modal || !closeElements || !form || !emailInput || !submitButton || !feedback || !eventContext) {
      return;
    }

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    let lastTrigger = null;
    let activeSignupData = null;

    const setFeedback = (state, message) => {
      feedback.dataset.state = state;
      feedback.textContent = message;
      feedback.hidden = false;
    };

    const clearFeedback = () => {
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.removeAttribute('data-state');
    };

    const closeSignupModal = () => {
      modal.setAttribute('hidden', '');
      document.body.classList.remove('events-archive-modal-open');
      document.removeEventListener('keydown', onEscape);
      if (lastTrigger) lastTrigger.focus();
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') closeSignupModal();
    };

    const openSignupModal = (trigger) => {
      if (!trigger) return;

      activeSignupData = {
        actionUrl: trigger.getAttribute('data-mailchimp-action-url') || '',
        audienceU: trigger.getAttribute('data-mailchimp-u') || '',
        audienceId: trigger.getAttribute('data-mailchimp-id') || '',
        tags: trigger.getAttribute('data-mailchimp-tags') || '',
        successMessage: trigger.getAttribute('data-mailchimp-success-message') || "You're on the list.",
        errorMessage:
          trigger.getAttribute('data-mailchimp-error-message') ||
          'Something went wrong. Please try again.',
      };

      const name = trigger.getAttribute('data-signup-event-title') || '';
      const location = trigger.getAttribute('data-signup-event-location') || '';
      const date = trigger.getAttribute('data-signup-event-date') || '';
      eventContext.textContent = [name, location, date].filter(Boolean).join(' - ');

      form.reset();
      clearFeedback();
      submitButton.disabled = false;
      lastTrigger = trigger;
      modal.removeAttribute('hidden');
      document.body.classList.add('events-archive-modal-open');
      document.addEventListener('keydown', onEscape);
      emailInput.focus();
    };

    closeElements.forEach((node) => {
      node.addEventListener('click', closeSignupModal);
    });

    const triggers = section.querySelectorAll('[data-signup-trigger="true"]');
    triggers.forEach((trigger) => {
      if (trigger.getAttribute('data-signup-ready') !== 'true') return;
      trigger.addEventListener('click', () => openSignupModal(trigger));
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearFeedback();

      const email = emailInput.value.trim();
      if (!email || !emailInput.checkValidity() || !activeSignupData) {
        setFeedback('error', 'Please enter a valid email address.');
        return;
      }

      submitButton.disabled = true;
      try {
        await submitMailchimpJsonp({
          actionUrl: activeSignupData.actionUrl,
          u: activeSignupData.audienceU,
          id: activeSignupData.audienceId,
          email,
          tags: activeSignupData.tags,
        });
        setFeedback('success', activeSignupData.successMessage || "You're on the list.");
        form.reset();
      } catch (error) {
        const message = sanitizeMessage(error?.message) || activeSignupData.errorMessage;
        setFeedback('error', message || 'Something went wrong. Please try again.');
      } finally {
        submitButton.disabled = false;
      }
    });
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const initMobileInViewReveal = (section) => {
    const cards = Array.from(section.querySelectorAll('.events-archive__past-card.has-thumbnail'));
    if (!cards.length) return () => {};
    let rafId = null;

    const clearActive = () => {
      cards.forEach((card) => {
        card.classList.remove('is-mobile-in-view');
        card.style.removeProperty('--events-inview-strength');
      });
    };

    const setMostVisibleActive = () => {
      const viewportHeight = Math.max(window.innerHeight || 0, 1);
      const sectionRect = section.getBoundingClientRect();
      const sectionProgress = clamp(
        (viewportHeight * 0.5 - sectionRect.top) / Math.max(sectionRect.height || 0, 1),
        0,
        1
      );
      const focusPoint = viewportHeight * (0.3 + sectionProgress * 0.4);

      const visibleCards = cards
        .map((card) => {
          const rect = card.getBoundingClientRect();
          const visiblePixels = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          if (visiblePixels <= 0 || rect.height <= 0) return null;

          const visibleRatio = clamp(visiblePixels / Math.min(rect.height, viewportHeight), 0, 1);
          const centerY = rect.top + rect.height / 2;
          const distanceRatio = 1 - clamp(Math.abs(centerY - focusPoint) / (viewportHeight * 0.8), 0, 1);
          const score = visibleRatio * 0.72 + distanceRatio * 0.28;

          return {
            card,
            score,
            visibleRatio,
          };
        })
        .filter(Boolean)
        .sort((left, right) => right.score - left.score || right.visibleRatio - left.visibleRatio);

      clearActive();
      const rankedCards = visibleCards.slice(0, 3);
      const rankBaseStrength = [0.8, 0.56, 0.34];
      const rankScoreBoost = [0.18, 0.16, 0.14];

      rankedCards.forEach(({ card, score, visibleRatio }, index) => {
        const strength = clamp(
          rankBaseStrength[index] + score * rankScoreBoost[index] + visibleRatio * 0.08,
          0.18,
          1
        );
        card.classList.add('is-mobile-in-view');
        card.style.setProperty('--events-inview-strength', strength.toFixed(3));
      });
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setMostVisibleActive();
      });
    };

    const handleScroll = () => requestUpdate();
    const handleResize = () => requestUpdate();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    requestUpdate();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      clearActive();
    };
  };

  const initSection = (section) => {
    if (!section || section.dataset.eventsArchiveInitialized === 'true') return;
    section.dataset.eventsArchiveInitialized = 'true';
    initCards(section);
    initModal(section);
    initSignupModal(section);
    section.__eventsArchiveCleanup = initMobileInViewReveal(section);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      initSection(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    initAll(target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const sections = target.matches(SECTION_SELECTOR)
      ? [target]
      : Array.from(target.querySelectorAll(SECTION_SELECTOR));

    sections.forEach((section) => {
      const cleanup = section.__eventsArchiveCleanup;
      if (typeof cleanup === 'function') cleanup();
      section.__eventsArchiveCleanup = null;
    });
  });
})();
